import {
  Button,
  Center,
  LoadingDots,
  Modal,
  ModalBody,
  ModalContainer,
  ModalDivider,
  ModalHeader,
  type ModalProps,
  Text,
  spacing,
} from '@retailer-platform/shared-components'
import React from 'react'
import { PermissionList } from 'domains/role-management/components/permission-list/PermissionList'
import { type Role } from 'domains/role-management/components/roles-search-list/utils/useRetailerRoles.hooks'
import { useGetRole } from 'domains/role-management/utils/hooks/useGetRole.hooks'
import { useDomainApolloErrorMessage } from 'domains/role-management/utils/domain/apollo'
import { useDomainGoToPath } from 'domains/role-management/utils/domain/routing'
import { useDomainMessages } from 'domains/role-management/utils/domain/intl'
import { useHasRoleManagementAccess } from 'domains/role-management/access-control/hasRoleManagementAccess.hooks'

/**
 * A 'smart' component that helps fetch and display a role's details.
 * Handy if you only have the role by ID. Otherwise, use `RoleManagementLoadedRoleOverviewModal`.
 * @param props the role to load
 */
export const RoleManagementRoleOverviewModal = (props: {
  roleId: string
  modalProps: Omit<ModalProps, 'isOpen'>
}) => {
  const { role, loading, error } = useGetRole({ roleId: props.roleId })
  const i18nError = useDomainApolloErrorMessage(error)

  if (loading) {
    return (
      <Modal isOpen={true} {...props.modalProps}>
        <Center fillParent style={{ minHeight: '50vh' }}>
          <LoadingDots />
        </Center>
      </Modal>
    )
  }

  if (error) {
    return (
      <Modal isOpen={true} {...props.modalProps}>
        <Center fillParent>{i18nError}</Center>
      </Modal>
    )
  }

  return <RoleManagementLoadedRoleOverviewModal role={role} modalProps={props.modalProps} />
}

/**
 * A 'dumb' component that displays a role's details, assuming you already have the role object.
 * If you only have the role ID, use `RoleManagementRoleOverviewModal`.
 */
export const RoleManagementLoadedRoleOverviewModal = (props: {
  role: Role
  modalProps: Omit<ModalProps, 'isOpen'>
}) => {
  const role = props.role

  const gotoUsers = useDomainGoToPath('role-management-role-assigned-users', { roleId: role.id })
  const i18n = useDomainMessages({
    viewUsers: 'roleManagementDomain.role.viewAssignedUsers',
    permissions: 'roleManagementDomain.role.permissions',
    roleDetail: 'roleManagementDomain.role.roleDetail',
    readOnlyRoleDescription: 'roleManagementDomain.role.readOnlyRoleDescription',
  })
  const hasAccessToRoleManagement = useHasRoleManagementAccess()
  const isReadOnlyRole = role.slug === 'legacy_read_only'

  return (
    <Modal
      isOpen={true}
      styleOverrides={{
        content: {
          minWidth: 'max(50vw, 600px)',
        },
      }}
      {...props.modalProps}
    >
      <ModalContainer>
        <ModalHeader>{i18n.roleDetail}</ModalHeader>
        <ModalBody style={{ flex: 'none' }}>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
              <Text size="large" weight="bold">
                {role.name}
              </Text>
              <Text>{role.description}</Text>
            </div>
            {hasAccessToRoleManagement && !isReadOnlyRole && (
              <Button variant="secondary" style={{ flex: 'none' }} onClick={gotoUsers}>
                {i18n.viewUsers}
              </Button>
            )}
          </div>
        </ModalBody>
        <ModalDivider />
        <ModalBody style={{ flex: 'none', padding: `24px 0 0 24px` }}>
          <Text size="large" weight="bold">
            {i18n.permissions}
          </Text>
        </ModalBody>
        <ModalBody style={{ gap: spacing.X24 }}>
          {(isReadOnlyRole && <Text>{i18n.readOnlyRoleDescription}</Text>) || (
            <PermissionList role={role} />
          )}
        </ModalBody>
      </ModalContainer>
    </Modal>
  )
}
